import { Button, Checkbox, Modal, Space, Typography } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDateOnly } from '../../helpers/DateHelper';
import { EurocupPlayerNameDobBackfillItemVm } from '../../utils/api';

interface Props {
    open: boolean;
    items: EurocupPlayerNameDobBackfillItemVm[];
    applying: boolean;
    onClose: () => void;
    onApply: (selectedEntityIds: number[]) => Promise<void>;
}

const formatIsoDate = (value?: Date): string => formatDateOnly(value, 'YYYY-MM-DD');

const NameDobBackfillPreviewModal: React.FC<Props> = ({
    open,
    items,
    applying,
    onClose,
    onApply,
}) => {
    const allIds = useMemo(
        () =>
            items
                .map((item) => item.externalSourceEntityId)
                .filter((id): id is number => id != null && id > 0),
        [items]
    );
    const [selected, setSelected] = useState<Set<number>>(() => new Set(allIds));

    useEffect(() => {
        if (open) {
            setSelected(new Set(allIds));
        }
    }, [open, allIds]);

    const toggle = (id: number, checked: boolean): void => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (checked) {
                next.add(id);
            } else {
                next.delete(id);
            }
            return next;
        });
    };

    const selectAll = (): void => setSelected(new Set(allIds));
    const selectNone = (): void => setSelected(new Set());

    const applySelected = (): void => {
        void onApply(Array.from(selected));
    };

    return (
        <Modal
            visible={open}
            title={`Name+DOB auto-map preview (${items.length})`}
            width={900}
            onCancel={onClose}
            footer={
                <Space>
                    <Button onClick={onClose} disabled={applying}>
                        Close
                    </Button>
                    <Button
                        type="primary"
                        disabled={applying || items.length === 0 || selected.size === 0}
                        loading={applying}
                        onClick={applySelected}
                    >
                        Apply selected ({selected.size})
                    </Button>
                </Space>
            }
        >
            {items.length === 0 ? (
                <Typography.Paragraph>No unmapped rows qualify.</Typography.Paragraph>
            ) : (
                <>
                    <Space style={{ marginBottom: 12 }}>
                        <Button size="small" onClick={selectAll} disabled={applying}>
                            Select all
                        </Button>
                        <Button size="small" onClick={selectNone} disabled={applying}>
                            Select none
                        </Button>
                    </Space>
                    <div style={{ maxHeight: 480, overflow: 'auto' }}>
                        {items.map((item) => {
                            const id = item.externalSourceEntityId || 0;
                            const euroBits = [
                                item.displayName || item.sourceKey || '?',
                                formatIsoDate(item.birthDate),
                                item.season,
                                item.teamOrClub,
                            ].filter((part): part is string => !!part);
                            const arenaName =
                                item.arenaPlayerDisplayName || `Player #${item.arenaPlayerId}`;
                            const arenaBits = [
                                arenaName,
                                formatIsoDate(item.arenaPlayerBirthDate),
                            ].filter((part): part is string => !!part);
                            return (
                                <div
                                    key={id || item.sourceKey}
                                    style={{
                                        marginBottom: 12,
                                        borderBottom: '1px solid #f0f0f0',
                                        paddingBottom: 8,
                                    }}
                                >
                                    <Checkbox
                                        checked={id > 0 && selected.has(id)}
                                        disabled={applying || id <= 0}
                                        onChange={(e): void => toggle(id, e.target.checked)}
                                    >
                                        <Typography.Text strong>Euro Cup</Typography.Text>
                                    </Checkbox>
                                    <div style={{ marginLeft: 24 }}>{euroBits.join(' · ')}</div>
                                    <Typography.Text type="secondary" style={{ marginLeft: 24 }}>
                                        {item.sourceKey}
                                        {item.matchedSourceKey
                                            ? ` · via ${item.matchedSourceKey}`
                                            : ''}
                                    </Typography.Text>
                                    <div style={{ marginTop: 6, marginLeft: 24 }}>
                                        <Typography.Text strong>Arena</Typography.Text>
                                        <div>
                                            {arenaBits.join(' · ')}{' '}
                                            <Link
                                                to={`/players/${item.arenaPlayerId}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                #{item.arenaPlayerId}
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </Modal>
    );
};

export default NameDobBackfillPreviewModal;
